import { LessonStatus } from '@/lib/constants';
import { handleSupabaseList, supabase } from '../../lib/supabase-unified';
import { Lesson } from '../../types';

export type SortOrder = 'by_date' | 'by_title' | 'by_position';

function withMainWorkTitle<T extends { work?: { title?: string | null } | null }>(
  lesson: T,
): Omit<T, 'work'> & { main_work_title: string | null } {
  const { work, ...rest } = lesson;
  return {
    ...rest,
    main_work_title: work?.title ?? null,
  };
}

export const fetchLessonsByCategories = async (
  categoryIds: number[],
  sortOrder: SortOrder = 'by_date',
  minStatus: number = LessonStatus.Published
): Promise<Lesson[]> => {
  // When ordering by position, we need to query from lesson_category to properly order the results
  if (sortOrder === 'by_position') {
    const lessonCategoryData = await handleSupabaseList<any>(
      supabase
        .from('lesson_categories')
        .select(`
          position,
          lesson:lessons!inner (
            id,
            title,
            short_title,
            week_number,
            main_work,
            slug,
            thumbnail,
            created_at,
            duration,
            video_count,
            course_id,
            published_at,
            summary,
            work:works!lessons_main_work_fkey (
              title
            )
          )
        `)
        .in('category', categoryIds)
        .not('lesson.published_at', 'is', null)
        .gte('lesson.status', minStatus)
        .order('position', { ascending: true }) as any
    );

    // Extract lessons from the nested structure
    return lessonCategoryData.map((item: any) => withMainWorkTitle(item.lesson) as Lesson);
  }

  // For other sort orders, query from lesson
  const query = supabase
    .from('lessons')
    .select(`
        id,
        title,
        short_title,
        week_number,
        main_work,
        slug,
        thumbnail,
        created_at,
        duration,
        video_count,
        course_id,
        published_at,
        summary,
        work:works!lessons_main_work_fkey (
          title
        ),
        lesson_categories!inner (
        category,
        position
      )
    `)
    .in('lesson_categories.category', categoryIds)
    .not('published_at', 'is', null)
    .gte('status', minStatus);

  // Apply sorting based on sortOrder
  if (sortOrder === 'by_title') {
    query.order('title', { ascending: true });
  } else {
    // Default: by_date (using created_at for backward compatibility)
    query.order('published_at', { ascending: false });
  }

  const results = await handleSupabaseList<any>(query as any);
  
  // Extract lesson data, removing the nested lesson_category structure
  return results.map((item: any) => {
    const { lesson_categories, ...lesson } = item;
    return withMainWorkTitle(lesson) as Lesson;
  });
};

export interface LessonNavigation {
  nextLesson: Lesson | null;
  previousLesson: Lesson | null;
  suggestedLessons: Lesson[];
}

export const getLessonNavigation = async (
  lessonId: number,
  userId: string
): Promise<LessonNavigation> => {
  try {
    const { data, error } = await supabase
      .rpc('get_lesson_navigation', {
        p_lesson_id: lessonId,
        p_user_id: userId
      });

    if (error) {
      throw error;
    }

    return data || {
      nextLesson: null,
      previousLesson: null,
      suggestedLessons: []
    };
  } catch (error) {
    // Return empty navigation on error
    return {
      nextLesson: null,
      previousLesson: null,
      suggestedLessons: []
    };
  }
};

// Keep old function for backward compatibility (deprecated)
export const fetchSuggestedNextLessons = async (
  lesson: Lesson,
  userId: string
): Promise<Lesson[]> => {
  // Use defaults for historical lessons without course_id or published_at
  const courseId = lesson.course_id ?? 25;
  const publishedAtStr = lesson.published_at || '2025-09-20T00:00:00.000Z';
  
  // Calculate date range: D - 18 days to D + 18 days
  const publishedDate = new Date(publishedAtStr);
  const startDate = new Date(publishedDate);
  startDate.setDate(startDate.getDate() - 18);
  const endDate = new Date(publishedDate);
  endDate.setDate(endDate.getDate() + 18);

  // Format dates for Supabase query
  const startDateStr = startDate.toISOString();
  const endDateStr = endDate.toISOString();

  // Get lessons in the date range from the same course
  // Use a LEFT JOIN with user_lessons and filter out completed ones
  const lessonsData = await handleSupabaseList<any>(
    supabase
      .from('lessons')
      .select(`
        id, 
        slug,
        title, 
        short_title,
        week_number,
        main_work,
        thumbnail, 
        created_at, 
        summary, 
        duration, 
        video_count,
        course_id,
        published_at,
        work:works!lessons_main_work_fkey (
          title
        ),
        user_lesson_interactions!left (
          lesson_id,
          user_id,
          is_completed
        )
      `)
      .eq('course_id', courseId)
      .gte('published_at', startDateStr)
      .lte('published_at', endDateStr)
      .neq('id', lesson.id) // Exclude the current lesson
      .not('published_at', 'is', null)
      .eq('user_lesson_interactions.user_id', userId)
      .eq('user_lesson_interactions.is_completed', true)
      .order('published_at', { ascending: true }) as any
  );

  if (lessonsData.length === 0) {
    return [];
  }

  // Filter out completed lessons
  // If user_lessons array has any entries, it means the lesson is completed by this user
  const suggestedLessons = lessonsData
    .filter((lessonData: any) => {
      const userLessons = lessonData.user_lessons || [];
      // Only include lessons that have no completed user_lessons entries
      return userLessons.length === 0;
    })
    .map((lessonData: any) => {
      // Remove the user_lessons join data from the result
      const { user_lessons, user_lesson_interactions, ...lesson } = lessonData;
      return withMainWorkTitle(lesson) as Lesson;
    });

  return suggestedLessons;
};

/**
 * Search lessons using full-text search
 * @param searchTerm - The search query
 * @param courseId - Optional course ID to filter results by course
 * @returns Array of matching lessons ordered by relevance
 */
export const searchLessonsByFulltext = async (
  searchTerm: string,
  courseId?: number,
  minStatus: number = LessonStatus.Published
): Promise<Lesson[]> => {
  return handleSupabaseList<Lesson>(
    supabase
      .rpc('lesson_fts_new_march', {
        search_term: searchTerm,
        p_course_id: courseId || null,
        p_min_status: minStatus
      }) as any
  );
};

/**
 * Escapes special characters in a string for use in PostgreSQL LIKE patterns
 * Escapes: %, _, and \ (the escape character itself)
 * @param str - The string to escape
 * @returns The escaped string safe for use in LIKE patterns
 */
function escapeLikePattern(str: string): string {
  // Escape backslash first (since we use it as escape character)
  // Then escape % and _ which are LIKE wildcards
  return str.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_');
}

// Search lessons within a course by keyword (title or summary)
export async function searchLessonsInCourseByLike(
  courseId: number,
  query: string,
  limit = 20,
  minStatus: number = LessonStatus.Published
): Promise<Lesson[]> {
  const q = query.trim();
  if (!q) return [];

  // We need two layers:
  // 1) Escape LIKE wildcard chars so user input is treated literally by ILIKE
  // 2) Encode only characters that break PostgREST's filter syntax (commas)
  //    Note: We can't use encodeURIComponent because it encodes spaces as %20,
  //    which PostgREST treats as literal %20 in LIKE patterns instead of spaces.
  const escapedForLike = escapeLikePattern(q);
  // Only encode commas (they're used as separators in .or() filter syntax)
  const encodedForPostgrest = escapedForLike.replace(/,/g, '%2C');
  // Keep the surrounding % wildcards unencoded so ILIKE still does "contains" matching.
  const pattern = `%${encodedForPostgrest}%`;

  const rows = await handleSupabaseList<any>(
    supabase
      .from('lessons')
      .select(`
        id,
        title,
        short_title,
        week_number,
        main_work,
        slug,
        thumbnail,
        summary,
        duration,
        video_count,
        course_id,
        published_at,
        work:works!lessons_main_work_fkey (
          title
        )
      `)
      .eq('course_id', courseId)
      .not('published_at', 'is', null)
      .gte('status', minStatus)
      .or(`title.ilike.${pattern},summary.ilike.${pattern}`)
      .order('published_at', { ascending: false })
      .limit(limit) as any
  );
  return rows.map((row) => withMainWorkTitle(row) as Lesson);
}

/**
 * Whether a published lesson with this slug exists in the given course.
 * Used to decide if a WordPress lesson link can open natively.
 */
export const publishedLessonExists = async (courseId: number, lessonSlug: string): Promise<boolean> => {
  const { data, error } = await supabase
    .from('lessons')
    .select('id')
    .eq('course_id', courseId)
    .eq('slug', lessonSlug)
    .eq('status', LessonStatus.Published)
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data != null;
};
